import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { AsyncPipe } from '@angular/common';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { isNull, isUndefined } from '@fcom/utils';

import { equals } from '../utils/equals';
import { LanguageService } from '../services/language.service';

/**
 * Asynchronous plural localization pipe
 * - Uses LanguageService to localize the input
 *
 * @example
 *
 * When having
 *
 * localizations = {
 *   foo: {
 *     singular: "{0} piece"
 *     plural: "{0} pieces"
 *   }
 * }
 *
 * The pipe would translate the following
 *
 * 'foo' | finPluralLocalization: 1 -> '1 piece',
 * 'foo' | finPluralLocalization: 2 -> '2 pieces'
 */
@Pipe({
  name: 'finPluralLocalization',
  pure: false,
})
export class PluralLocalizationPipe implements PipeTransform, OnDestroy {
  private async: AsyncPipe;
  private cached$: Observable<string>;
  private cachedKey: string;
  private cachedCount: number;

  constructor(
    private ref: ChangeDetectorRef,
    private languageService: LanguageService
  ) {
    this.async = new AsyncPipe(this.ref);
  }

  transform(key: string, count: number): string {
    if (isUndefined(key) || isNull(key) || typeof count !== 'number') {
      return undefined;
    }

    if (this.valueChanged(key, count)) {
      this.update(key, count);
    }

    return this.async.transform(this.cached$);
  }

  ngOnDestroy(): void {
    this.async.ngOnDestroy();
  }

  private valueChanged(key: string, count: number): boolean {
    return !this.cached$ || key !== this.cachedKey || !equals(count, this.cachedCount);
  }

  private update(key: string, count: number): void {
    const keyToUse = count === 1 ? `${key}.singular` : `${key}.plural`;
    this.cached$ = this.languageService.translate(keyToUse, [count]).pipe(map((str) => (str ? str : '')));
    this.cachedKey = key;
    this.cachedCount = count;
  }
}
